import { FC, useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Box,
  Fab,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LeftIcon from '@mui/icons-material/ChevronLeft';
import RightIcon from '@mui/icons-material/ChevronRight';
import { Format } from 'helpers/Format';
import { Button } from 'components';
import { useHistory, useRouteMatch } from 'react-router';
import { LinearProgress } from 'components/LinearProgress'; 

interface IVets {
  vets: any[];
  currentPage: number;
  setPage: Function;
  maxPage: number;
  loading: boolean;
}
interface IVetRow {
  vet: any;
}

const VetRow: FC<IVetRow> = ({ vet }) => {
  let history = useHistory();
  let { path } = useRouteMatch();
  const [open, setOpen] = useState(false);
  const createdAtDateObj = new Date(vet.createdAt);
  const createdAt = `${createdAtDateObj.getDate()}/${
    createdAtDateObj.getMonth() + 1
  }/${createdAtDateObj.getFullYear()}`;

  const handleEdit = () => {
    delete vet.clinicName;
    delete vet.presencialTreatment;
    
    history.push(`${path}/edit`, {
      ...vet,
    });
  };

  return (
    <>
      <TableRow
        key={vet.name}
        sx={{
          '&:hover, &:hover + *': { backgroundColor: '#ddd' },
        }}
      >
        <TableCell component="th" scope="row">
          {vet.name}
        </TableCell>
        <TableCell>{Format.cpf(vet.cpf)}</TableCell>
        <TableCell>{vet.bond === 'internal' ? 'Interno' : 'Externo'}</TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, border: 0 }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table size="small" aria-label="more-info">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>
                      <strong>CRMV:</strong> {vet.crmv}
                    </Typography>
                    <Typography>
                      <strong>Especialidade:</strong> {vet.specialization}
                    </Typography>
                    <Typography>
                      <strong>Data de Vínculo:</strong> {createdAt}
                    </Typography>
                    <Typography>
                      <strong>Conta:</strong>{' '}
                      {vet.active ? 'Ativada' : 'Desativada'}
                    </Typography>
                    <Typography>
                      <strong>Cadastro:</strong>{' '}
                      {vet.registrationCompleted ? 'Atualizado' : 'Pendente'}
                    </Typography>
                    <Typography>
                      <strong>E-mail:</strong> {vet.email}
                    </Typography>
                    <Box py={1}>
                      <Button onClick={handleEdit}>Editar</Button>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography sx={{ fontSize: 40 }}>
                        {vet.totalTreatments}
                      </Typography>
                      <Typography>Atendimentos</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: 40 }}>
                      {Format.timeToHourMinute(vet.hoursWorked)}
                    </Typography>
                    <Typography>
                      {vet.hoursWorked > 1
                        ? 'Horas corridas'
                        : 'Minutos corridos'}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const VetList: FC<IVets> = ({
  vets,
  currentPage,
  setPage,
  maxPage,
  loading,
}) => {
  const previousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < maxPage) {
      setPage(currentPage + 1);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [maxPage, setPage]);

  return (
    <TableContainer component={Paper}>
      {loading && <LinearProgress />}
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Nome</strong>
            </TableCell>
            <TableCell>
              <strong>CPF</strong>
            </TableCell>
            <TableCell>
              <strong>Vínculo</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Ver mais</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vets?.map((vet, index) => (
            <VetRow key={index} vet={vet} />
          ))}
          <TableRow>
            <TableCell align="right" colSpan={4}>
              <Typography>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="remove"
                  disabled={currentPage <= 1}
                  onClick={previousPage}
                >
                  <LeftIcon />
                </Fab>
                <Fab
                  disabled
                  size="small"
                  aria-label="add"
                  sx={{ ml: 1, mr: 1 }}
                >
                  {currentPage}
                </Fab>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="add"
                  disabled={currentPage === maxPage}
                  onClick={nextPage}
                >
                  <RightIcon />
                </Fab>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

